import React from 'react';
import { isActivePage, setActivePage } from '../../services/activePage';

/**
 * The list of Pro pages an account can act as — shared by the account menu's
 * ACT AS row and the top-bar strip's SWITCH PAGE. One row per page: square
 * logo/letter, name, kind, a role pill, and the active one marked.
 * `onNew` adds a `+ NEW PRO PAGE` row at the bottom (→ /pro/setup?new=1).
 */
export const ROLE_LABEL = { owner: 'OWNER', admin: 'ADMIN', member: 'MEMBER' };
export const KIND_WORD = { bookable: 'BOOKABLE', eatery: 'EATERY', bar: 'BAR', venue: 'VENUE', shop: 'SHOP' };

/** Wear a page: remember the persona, then land on it. A full reload so every surface re-reads the persona and the X-Pro-Page header. */
export function actAs(page) {
  if (!page?.slug) return;
  setActivePage({ id: page.id, slug: page.slug, name: page.name, avatarUrl: page.logoUrl || page.avatarUrl || null, kind: page.kind, role: page.role });
  window.location.assign(`/pro/${page.slug}`);
}

/** Square logo, or the first letter of the name. */
export function PageSquare({ page, size = 36, className = '' }) {
  const src = page?.logoUrl || page?.avatarUrl || null;
  return (
    <div className={`flex flex-shrink-0 items-center justify-center overflow-hidden border-2 border-black bg-gray-100 ${className}`} style={{ width: size, height: size }}>
      {src ? <img src={src} alt="" className="h-full w-full object-cover" /> : <span className="font-mono font-bold" style={{ fontSize: Math.round(size * 0.4) }}>{(page?.name || '?').charAt(0).toUpperCase()}</span>}
    </div>
  );
}

export default function ProPagePicker({ pages = [], active = null, onPick = actAs, onNew, testId = 'pro-page-picker' }) {
  return (
    <div data-testid={testId}>
      {pages.map((p) => {
        const on = isActivePage(p, active);
        return (
          <button
            key={p.id || p.slug}
            type="button"
            onClick={() => (on ? null : onPick?.(p))}
            aria-current={on ? 'true' : undefined}
            data-testid={`page-row-${p.id || p.slug}`}
            className="group flex w-full items-center gap-3 px-5 py-2.5 text-left transition-colors hover:bg-black hover:text-white"
          >
            <PageSquare page={p} className="group-hover:border-white" />
            <div className="min-w-0 flex-1">
              <div className="truncate font-mono text-xs font-bold uppercase tracking-wider">{p.name}</div>
              <div className="truncate font-mono text-[10px] uppercase tracking-widest text-gray-500 group-hover:text-gray-300">
                {KIND_WORD[p.kind] || 'PAGE'}{p.isPublished === false ? ' · DRAFT' : ''}
              </div>
            </div>
            {on && <span className="flex-shrink-0 font-mono text-[9px] font-bold uppercase tracking-widest" style={{ color: '#FF00FF' }}>ACTIVE</span>}
            <span className="flex-shrink-0 border-2 border-current px-1.5 font-mono text-[9px] font-bold uppercase leading-[1.6] tracking-widest">{ROLE_LABEL[p.role] || 'MEMBER'}</span>
          </button>
        );
      })}
      {onNew && (
        <button
          type="button"
          onClick={onNew}
          data-testid="new-pro-page"
          className="flex w-full items-center gap-3 border-t-2 border-dashed border-black px-5 py-3 text-left font-mono text-xs font-bold uppercase tracking-wider transition-colors hover:bg-black hover:text-white"
        >
          + New Pro page
        </button>
      )}
    </div>
  );
}
